import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { DiaryHomework } from '../types';
import { errorToast } from '../../../../CommonComponent/toast';
import useFetchWithAuth from '../../../../CommonHooks/useFetchWithAuth';
import { noticeBaseUrl } from '../../../../baseUrl';
import { useMainContainerProvider } from '../../../../mainContainerProvider';
import { initialDiaryState } from '../constant';

interface DairiesContextType {
  files: File[] | string[];
  setFiles: React.Dispatch<React.SetStateAction<File[] | string[]>>;
  setLocalDiaries: React.Dispatch<React.SetStateAction<DiaryHomework>>;
  localDiaries: DiaryHomework;
  handleSubmit: () => Promise<void>;
  isLoading: boolean;
  setOpenSendDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openSendDialog: boolean;
  setHomeworkList: React.Dispatch<React.SetStateAction<DiaryHomework[] | undefined>>;
  homeworkList: DiaryHomework[] | undefined;
  setIsListLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isListLoading: boolean;
  setOpenCreateDiaries: React.Dispatch<React.SetStateAction<boolean>>;
  openCreateDiaries: boolean;
  setOldFiles: React.Dispatch<React.SetStateAction<string[]>>;
  oldFiles: string[];
  handleGetHomeworkList: () => Promise<void>;
}

const DairiesContext = createContext<DairiesContextType | undefined>(undefined);
const DairiesProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<File[] | string[]>([]);
  const [oldFiles, setOldFiles] = useState<string[]>([]);
  const [openSendDialog, setOpenSendDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [localDiaries, setLocalDiaries] = useState<DiaryHomework>(initialDiaryState);
  const [homeworkList, setHomeworkList] = useState<DiaryHomework[]>();
  const { fetchWithAuth } = useFetchWithAuth();
  const { uid, userType, instituteId, studentClass, section } = useMainContainerProvider();
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [openCreateDiaries, setOpenCreateDiaries] = useState(false);

  const handleGetHomeworkList = async () => {
    try {
      setIsListLoading(true);
      const { error, data } = await fetchWithAuth(
        `search-homework/?instituteId=${instituteId}&studentClass=${studentClass !== 'undefined' ? studentClass : ''}&section=${
          section !== 'undefined' ? section : ''
        }`,
        {},
        noticeBaseUrl
      );
      if (error) {
        errorToast('Something went wrong');
      } else {
        setHomeworkList(data || []);
      }
    } catch (e) {
      errorToast('Failed to fetch homework list');
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    if (homeworkList === undefined) {
      handleGetHomeworkList();
    }
    // eslint-disable-next-line
  }, [homeworkList]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append(`files`, file);
      });
      formData.append(
        'oldFiles',
        JSON.stringify(
          oldFiles.map((item) => {
            return Object.keys(item)[0];
          })
        )
      );
      formData.append('id', localDiaries.id !== undefined ? String(localDiaries.id) : '');
      formData.append('subject', localDiaries.subject);
      formData.append('content', localDiaries.content);
      formData.append('createdBy', uid);
      formData.append('diaryContent', localDiaries.diaryContent);
      formData.append('homeworkDate', localDiaries.homeworkDate);
      formData.append('studentClass', localDiaries.studentClass);
      formData.append('section', localDiaries.section);
      formData.append('instituteId', instituteId);
      formData.append('createdFor', localDiaries.specificStudent && localDiaries.specificStudent.length > 0 ? 'student' : 'class');
      formData.append('specificStudent', JSON.stringify(localDiaries.specificStudent));
      const { error, data } = await fetchWithAuth(
        'diaryhomework/',
        {
          method: 'POST',
          body: formData,
        },
        noticeBaseUrl
      );
      if (error) {
        errorToast(error);
        return;
      }
      setLocalDiaries(data?.data?.[0] || initialDiaryState);
      setOpenSendDialog(false);
      setFiles([]);
      setOldFiles([]);
      handleGetHomeworkList();
    } catch (e) {
      console.error(e);
      errorToast('Something went wrong while updating diaries');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [fetchWithAuth, files, localDiaries, uid, userType, oldFiles]);

  return (
    <DairiesContext.Provider
      value={{
        files,
        setFiles,
        setLocalDiaries,
        localDiaries,
        handleSubmit,
        isLoading,
        setOpenSendDialog,
        openSendDialog,
        setHomeworkList,
        homeworkList,
        setIsListLoading,
        isListLoading,
        setOpenCreateDiaries,
        openCreateDiaries,
        setOldFiles,
        oldFiles,
        handleGetHomeworkList,
      }}
    >
      {children}
    </DairiesContext.Provider>
  );
};

export const useDairiesProvider = (): DairiesContextType => {
  const context = useContext(DairiesContext);
  if (!context) {
    throw new Error('useDairiesProvider must be used within a DairiesProvider');
  }
  return context;
};

export default DairiesProvider;
